import { createRouter, createWebHistory } from 'vue-router'
import type { RouteRecordRaw } from 'vue-router'

const routes: RouteRecordRaw[] = [
  {
    path: '/',
    name: 'home',
    component: () => import('@/views/HomeView.vue'),
    meta: { layout: 'default' }
  },
  {
    path: '/pricing',
    name: 'pricing',
    component: () => import('@/views/subscription/PricingView.vue'),
    meta: { layout: 'default' }
  },
  {
    path: '/auth/login',
    name: 'login',
    component: () => import('@/views/auth/LoginView.vue'),
    meta: { layout: 'auth', guestOnly: true }
  },
  {
    path: '/auth/register',
    name: 'register',
    component: () => import('@/views/auth/RegisterView.vue'),
    meta: { layout: 'auth', guestOnly: true }
  },
  {
    path: '/auth/callback',
    name: 'auth-callback',
    component: () => import('@/views/auth/CallbackView.vue'),
    meta: { layout: 'auth' }
  },
  {
    path: '/auth/forgot-password',
    name: 'forgot-password',
    component: () => import('@/views/auth/ForgotPasswordView.vue'),
    meta: { layout: 'auth', guestOnly: true }
  },
  {
    path: '/auth/reset-password',
    name: 'reset-password',
    component: () => import('@/views/auth/ResetPasswordView.vue'),
    meta: { layout: 'auth', guestOnly: true }
  },
  {
    path: '/dashboard',
    name: 'dashboard',
    component: () => import('@/views/tribute/TributeListView.vue'),
    meta: { layout: 'default', requiresAuth: true }
  },
  {
    path: '/dashboard/create',
    name: 'tribute-create',
    component: () => import('@/views/tribute/TributeCreateView.vue'),
    meta: { layout: 'default', requiresAuth: true }
  },
  {
    path: '/dashboard/edit/:id',
    name: 'tribute-edit',
    component: () => import('@/views/tribute/TributeEditView.vue'),
    meta: { layout: 'default', requiresAuth: true }
  },
  {
    path: '/dashboard/family/:tributeId',
    name: 'family-space',
    component: () => import('@/views/family/FamilySpaceView.vue'),
    meta: { layout: 'default', requiresAuth: true }
  },
  {
    path: '/settings',
    name: 'settings',
    component: () => import('@/views/settings/SettingsView.vue'),
    meta: { layout: 'default', requiresAuth: true }
  },
  {
    path: '/admin',
    name: 'admin-login',
    component: () => import('@/views/admin/AdminLoginView.vue'),
    meta: { layout: 'blank', guestOnly: true }
  },
  {
    path: '/admin/dashboard',
    name: 'admin-dashboard',
    component: () => import('@/views/admin/AdminDashboardView.vue'),
    meta: { layout: 'blank', requiresAuth: true, requiresAdmin: true }
  },
  {
    path: '/subscription/success',
    name: 'subscription-success',
    component: () => import('@/views/subscription/SubscriptionSuccessView.vue'),
    meta: { layout: 'default', requiresAuth: true }
  },
  {
    path: '/subscription/cancel',
    name: 'subscription-cancel',
    redirect: '/pricing'
  },
  {
    path: '/tribute/:slug',
    name: 'tribute-public',
    component: () => import('@/views/tribute/TributePublicView.vue'),
    meta: { layout: 'tribute' }
  },
  {
    path: '/qr/:qrCodeId',
    name: 'qr-redirect',
    component: () => import('@/views/QRRedirectView.vue')
  },
  {
    path: '/activation/:code',
    name: 'activation-redirect',
    component: () => import('@/views/activation/ActivationRedirectView.vue')
  },
  {
    path: '/activate/:code',
    name: 'activate',
    component: () => import('@/views/activation/ActivateView.vue'),
    meta: { layout: 'blank' }
  },
  {
    path: '/invite/:token',
    name: 'invite-accept',
    component: () => import('@/views/family/InviteAcceptView.vue'),
    meta: { layout: 'blank' }
  },
  // Distributor pages
  {
    path: '/distributeur/setup',
    name: 'distributor-setup',
    component: () => import('@/views/distributor/DistributorSetupView.vue'),
    meta: { layout: 'blank' }
  },
  {
    path: '/distributeur/login',
    name: 'distributor-login',
    component: () => import('@/views/distributor/DistributorLoginView.vue'),
    meta: { layout: 'blank' }
  },
  {
    path: '/distributeur/reset-password',
    name: 'distributor-reset-password',
    component: () => import('@/views/distributor/DistributorResetPasswordView.vue'),
    meta: { layout: 'blank' }
  },
  {
    path: '/distributeur/dashboard',
    name: 'distributor-dashboard',
    component: () => import('@/views/distributor/DistributorDashboardView.vue'),
    meta: { layout: 'blank', requiresDistributor: true }
  },
  // Legal pages (accessible to everyone)
  {
    path: '/legal/terms',
    name: 'terms',
    component: () => import('@/views/legal/TermsView.vue'),
    meta: { layout: 'default' }
  },
  {
    path: '/legal/privacy',
    name: 'privacy',
    component: () => import('@/views/legal/PrivacyView.vue'),
    meta: { layout: 'default' }
  },
  {
    path: '/legal/mentions',
    name: 'mentions',
    component: () => import('@/views/legal/MentionsView.vue'),
    meta: { layout: 'default' }
  },
  {
    path: '/legal/cookies',
    name: 'cookies',
    component: () => import('@/views/legal/CookiesView.vue'),
    meta: { layout: 'default' }
  },
  {
    path: '/legal/gdpr',
    name: 'gdpr',
    component: () => import('@/views/legal/GDPRView.vue'),
    meta: { layout: 'default' }
  },
  {
    path: '/contact',
    name: 'contact',
    component: () => import('@/views/ContactView.vue'),
    meta: { layout: 'default' }
  },
  {
    path: '/waitlist',
    name: 'waitlist',
    component: () => import('@/views/WaitlistView.vue'),
    meta: { layout: 'default' }
  },
  // Blog pages
  {
    path: '/blog',
    name: 'blog',
    component: () => import('@/views/blog/BlogView.vue'),
    meta: { layout: 'default' }
  },
  {
    path: '/blog/jean-moulin-heros-resistance',
    name: 'blog-article-jean-moulin',
    component: () => import('@/views/blog/ArticleJeanMoulinView.vue'),
    meta: { layout: 'default' }
  },
  {
    path: '/blog/:slug',
    name: 'blog-article',
    component: () => import('@/views/blog/BlogArticleView.vue'),
    meta: { layout: 'default' }
  },
  // Company pages
  {
    path: '/about',
    name: 'about',
    component: () => import('@/views/company/AboutView.vue'),
    meta: { layout: 'default' }
  },
  {
    path: '/mission',
    name: 'mission',
    component: () => import('@/views/company/MissionView.vue'),
    meta: { layout: 'default' }
  },
  {
    path: '/team',
    name: 'team',
    component: () => import('@/views/company/TeamView.vue'),
    meta: { layout: 'default' }
  },
  {
    path: '/careers',
    name: 'careers',
    component: () => import('@/views/company/CareersView.vue'),
    meta: { layout: 'default' }
  },
  {
    path: '/press',
    name: 'press',
    component: () => import('@/views/company/PressView.vue'),
    meta: { layout: 'default' }
  },
  // Shop pages
  {
    path: '/shop/qr-box',
    name: 'shop-qr-box',
    component: () => import('@/views/shop/QRBoxProductView.vue'),
    meta: { layout: 'default' }
  },
  // Product pages
  {
    path: '/features',
    name: 'features',
    component: () => import('@/views/product/FeaturesView.vue'),
    meta: { layout: 'default' }
  },
  {
    path: '/how-it-works',
    name: 'how-it-works',
    component: () => import('@/views/product/HowItWorksView.vue'),
    meta: { layout: 'default' }
  },
  {
    path: '/testimonials',
    name: 'testimonials',
    component: () => import('@/views/product/TestimonialsView.vue'),
    meta: { layout: 'default' }
  },
  {
    path: '/faq',
    name: 'faq',
    component: () => import('@/views/product/FAQView.vue'),
    meta: { layout: 'default' }
  },
  // Resources pages
  {
    path: '/guides',
    name: 'guides',
    component: () => import('@/views/resources/GuidesView.vue'),
    meta: { layout: 'default' }
  },
  {
    path: '/help',
    name: 'help',
    component: () => import('@/views/resources/HelpView.vue'),
    meta: { layout: 'default' }
  },
  {
    path: '/tutorials',
    name: 'tutorials',
    component: () => import('@/views/resources/TutorialsView.vue'),
    meta: { layout: 'default' }
  },
  {
    path: '/:pathMatch(.*)*',
    name: 'not-found',
    component: () => import('@/views/NotFoundView.vue')
  }
]

const router = createRouter({
  history: createWebHistory(),
  routes,
  scrollBehavior(_to, _from, savedPosition) {
    if (savedPosition) {
      return savedPosition
    }
    return { top: 0 }
  }
})

// Get storage key prefix based on subdomain
function getStoragePrefix(): string {
  const hostname = window.location.hostname
  if (hostname === 'admin.hommageqr.com') return 'admin_'
  if (hostname === 'partenaires.hommageqr.com') return 'partenaires_'
  return ''
}

// Navigation guard for auth
router.beforeEach((to, _from, next) => {
  // Check subdomains first
  const isAdminSubdomain = window.location.hostname === 'admin.hommageqr.com'
  const isPartenairesSubdomain = window.location.hostname === 'partenaires.hommageqr.com'

  // Use subdomain-specific storage keys
  const prefix = getStoragePrefix()
  const tokenKey = `${prefix}token`
  const userKey = `${prefix}user`
  const distributorTokenKey = `${prefix}distributorToken`

  const isAuthenticated = !!localStorage.getItem(tokenKey)
  const userStr = localStorage.getItem(userKey)
  const user = userStr ? JSON.parse(userStr) : null
  const isSuperAdmin = user?.isSuperAdmin === true

  // Distributor auth check (moved up for subdomain checks)
  const isDistributorAuthenticated = !!localStorage.getItem(distributorTokenKey)

  // On admin subdomain, redirect root to admin login
  if (isAdminSubdomain && to.path === '/') {
    if (isAuthenticated && isSuperAdmin) {
      next({ name: 'admin-dashboard' })
    } else {
      next({ name: 'admin-login' })
    }
    return
  }

  // On admin subdomain, block non-admin routes
  if (isAdminSubdomain && !to.path.startsWith('/admin')) {
    next({ name: 'admin-login' })
    return
  }

  // On partenaires subdomain, redirect root to distributor login
  if (isPartenairesSubdomain && to.path === '/') {
    if (isDistributorAuthenticated) {
      next({ name: 'distributor-dashboard' })
    } else {
      next({ name: 'distributor-login' })
    }
    return
  }

  // On partenaires subdomain, block non-distributor routes
  if (isPartenairesSubdomain && !to.path.startsWith('/distributeur')) {
    next({ name: 'distributor-login' })
    return
  }

  // Handle distributor routes
  if (to.meta.requiresDistributor) {
    if (!isDistributorAuthenticated) {
      next({ name: 'distributor-login' })
      return
    }
  }

  // Redirect authenticated distributors from login page to dashboard
  if (to.name === 'distributor-login' && isDistributorAuthenticated) {
    next({ name: 'distributor-dashboard' })
    return
  }

  // Handle admin login page - redirect authenticated admins to dashboard
  if (to.name === 'admin-login' && isAuthenticated && isSuperAdmin) {
    next({ name: 'admin-dashboard' })
    return
  }

  // Redirect authenticated users away from guest-only pages (home, login, register)
  // But not for admin-login (handled above)
  if (to.meta.guestOnly && isAuthenticated && to.name !== 'admin-login') {
    next({ name: 'dashboard' })
    return
  }

  // Redirect unauthenticated users away from protected pages
  if (to.meta.requiresAuth && !isAuthenticated) {
    // For admin routes, redirect to admin login
    if (to.meta.requiresAdmin) {
      next({ name: 'admin-login' })
    } else {
      next({ name: 'login', query: { redirect: to.fullPath } })
    }
    return
  }

  // Check admin access for admin-only routes
  if (to.meta.requiresAdmin && !isSuperAdmin) {
    next({ name: 'admin-login' })
    return
  }

  next()
})

export default router
